import { useEffect, useRef, useState } from 'react'
import {
  dateTimeFormat,
  formatBytes,
  licenseNotice,
  mayCreate,
  VISIBILITY_LABEL,
  type BoardDto,
  type MeDto,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Link, useNavigate } from '../router.js'
import { useLanguage, useText } from '../i18n.js'
import { LegalFooter } from '../components/LegalFooter.js'
import { Brandmark } from '../components/Brandmark.js'
import { AccountDialog } from '../components/AccountDialog.js'
import { AdminDialog } from '../components/AdminDialog.js'
import { TeamDialog } from '../components/TeamDialog.js'
import { OrgDesignDialog } from '../components/OrgDesignDialog.js'
import { NewBoardDialog, type BoardDraft } from '../components/NewBoardDialog.js'
import { rememberInitialDesign } from '../collab/initialDesign.js'
import { useFontStylesheet } from '../fonts.js'
import { useOrgs } from '../useOrgs.js'

export function BoardsPage({
  user,
  onSignedOut,
}: {
  user: MeDto
  onSignedOut: () => void
}): React.ReactElement {
  const t = useText()
  const { language } = useLanguage()
  const dateFormat = dateTimeFormat(language)
  const { orgs, current, select, reload } = useOrgs()
  const [boards, setBoards] = useState<BoardDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showTeam, setShowTeam] = useState(false)
  const [showDesign, setShowDesign] = useState(false)
  const [showNewBoard, setShowNewBoard] = useState(false)
  const [showAccount, setShowAccount] = useState(false)
  const [showAdmin, setShowAdmin] = useState(false)
  const [busyBoardId, setBusyBoardId] = useState<string | null>(null)
  const navigate = useNavigate()

  // Die Schriften der gewählten Organisation, damit die Vorschau im
  // Anlege-Fenster schon in der richtigen Schrift steht.
  useFontStylesheet(current?.id)

  // Was die Übersicht zeigt, kann weniger sein als die Organisation belegt:
  // private Boards anderer zählen mit, erscheinen hier aber nicht.
  const visibleBytes = (boards ?? []).reduce((sum, board) => sum + board.storageBytes, 0)
  const hiddenBytes = boards === null ? 0 : Math.max(0, (current?.storageBytes ?? 0) - visibleBytes)

  const notice = current ? licenseNotice(current.license, t) : null
  const canCreate = current ? mayCreate(current.license.state) : false

  /*
   * Der Übersetzer steht bewusst **nicht** in den Abhängigkeiten.
   *
   * Er ist zwar stabil, wechselt aber mit der Sprache — und ein Sprachwechsel im
   * Konto-Fenster würde die Liste sonst leeren und neu laden. Gebraucht wird er
   * hier nur im Fehlerfall, und der tritt beim Umschalten nicht ein.
   */
  const text = useRef(t)
  text.current = t

  useEffect(() => {
    if (!current) return
    setBoards(null)
    setError(null)
    api
      .boards(current.id)
      .then(setBoards)
      .catch(() => setError(text.current('boards.loadFailed')))
  }, [current])

  async function createBoard(draft: BoardDraft): Promise<void> {
    if (!current) return
    const board = await api.createBoard(current.id, draft.title)
    // Untergrund, Schriften und das gewählte Gerüst liegen im Yjs-Dokument;
    // geschrieben werden sie erst, wenn die Board-Ansicht es geöffnet hat.
    rememberInitialDesign(board.id, {
      background: draft.background,
      theme: draft.theme,
      template: draft.template,
    })
    navigate(`/b/${board.id}`)
  }

  /**
   * Ein Board mit allem, was darin liegt, kopieren.
   *
   * Die Kopie wird nicht gleich geöffnet: Wer dupliziert, will meistens gleich
   * das nächste duplizieren oder umbenennen — und ein Sprung ins Board risse
   * ihn aus der Übersicht.
   */
  async function duplicate(id: string): Promise<void> {
    setBusyBoardId(id)
    setError(null)
    try {
      const copy = await api.duplicateBoard(id)
      setBoards((list) => [copy, ...(list ?? [])])
      void reload()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('boards.copyFailed'))
    } finally {
      setBusyBoardId(null)
    }
  }

  return (
    <div className="shell brand-surface">
      <header className="top-bar">
        <Link to="/boards">
          <Brandmark size="small" />
        </Link>
        <div className="top-bar-right">
          {orgs && orgs.length > 0 && (
            <label className="org-switch">
              <span className="sr-only">{t('common.organisation')}</span>
              <select
                value={current?.id ?? ''}
                onChange={(event) => select(event.target.value)}
                title={t('boards.switchOrg')}
              >
                {orgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {current && (
            <>
              <button className="button ghost" onClick={() => setShowTeam(true)}>
                {t('common.organisation')}
              </button>
              <button
                className="button ghost"
                onClick={() => setShowDesign(true)}
                title={t('boards.designTitle')}
              >
                {t('boards.design')}
              </button>
            </>
          )}
          {user.isPlatformAdmin && (
            <button
              className="button ghost"
              onClick={() => setShowAdmin(true)}
              title={t('boards.customersTitle')}
            >
              {t('boards.customers')}
            </button>
          )}
          {/*
            Bewusst ein gewöhnlicher Link statt <Link>: Das Handbuch ist eine
            eigene Seite im Bundle, keine Route der App. Der Router würde nur
            die URL ändern und den Fallback auf die SPA auslösen.
          */}
          <a
            className="button ghost"
            href={t('manual.path')}
            target="_blank"
            rel="noreferrer"
            title={t('boards.manualTitle')}
          >
            {t('boards.manual')}
          </a>
          <button
            className="button ghost"
            onClick={() => setShowAccount(true)}
            title={t('boards.accountTitle')}
          >
            {user.displayName}
          </button>
          <button
            className="button ghost"
            onClick={async () => {
              await api.logout()
              onSignedOut()
            }}
          >
            {t('boards.signOut')}
          </button>
        </div>
      </header>

      <main className="boards">
        <div className="boards-head">
          <div>
            <h2>{t('boards.title')}</h2>
            <p className="boards-lead">
              {current ? t('boards.lead', { org: current.name }) : t('boards.leadPlain')}
            </p>
            {current && (
              <p className="storage-line">
                <span className="storage-value">
                  {formatBytes(current.storageBytes, language)}
                </span>{' '}
                <span className="muted small">
                  {t('boards.storageUsed')}
                  {/*
                    Der Zusatz erscheint nur, wenn er etwas sagt: Sind beide Zahlen
                    gleich, stünde dieselbe Angabe zweimal da. Weichen sie ab, liegt
                    der Rest in privaten Boards anderer — und das ist die Auskunft,
                    die man beim Aufräumen wirklich braucht.
                  */}
                  {hiddenBytes > 0 &&
                    t('boards.storageHidden', { size: formatBytes(hiddenBytes, language) })}
                </span>
              </p>
            )}
          </div>
          <button
            className="button primary"
            onClick={() => setShowNewBoard(true)}
            disabled={!current || !canCreate}
            title={canCreate ? undefined : t('license.expired.short')}
          >
            {t('boards.new')}
          </button>
        </div>

        {/*
          Der Hinweis steht über der Liste und nicht als Fenster davor: Die
          Boards bleiben ja benutzbar — es soll drücken, nicht blockieren.
        */}
        {notice && (
          <p className={`licence-notice is-${current?.license.state}`} role="status">
            {notice}
          </p>
        )}

        {error && <p className="form-error">{error}</p>}
        {boards === null && !error && <p className="muted">{t('common.loading')}</p>}

        {boards?.length === 0 && (
          <div className="empty-state">
            <h3>{t('boards.emptyTitle')}</h3>
            <p>{t('boards.emptyLead')}</p>
          </div>
        )}

        <ul className="board-grid">
          {boards?.map((board) => (
            <li key={board.id}>
              <Link to={`/b/${board.id}`} className="board-card">
                {board.previewUrl ? (
                  <img className="board-card-preview" src={board.previewUrl} alt="" loading="lazy" />
                ) : (
                  <span className="board-card-preview is-empty" aria-hidden="true" />
                )}
                <span className="board-card-title">{board.title}</span>
                <span className="board-card-meta">
                  {board.ownerName} · {dateFormat.format(new Date(board.updatedAt))}
                </span>
                <span className="board-card-access" title={t(VISIBILITY_LABEL[board.visibility])}>
                  <span>
                    {board.visibility === 'organisation'
                      ? t('boards.wholeOrg')
                      : board.sharedWith === 0
                        ? t('boards.private')
                        : t('boards.privateWith', {
                            people: t.plural(
                              board.sharedWith + 1,
                              'common.person.one',
                              'common.person.many',
                            ),
                          })}
                  </span>
                  <span className="board-card-size" title={t('boards.sizeTitle')}>
                    {formatBytes(board.storageBytes, language)}
                  </span>
                </span>
              </Link>
              {/*
                Neben der Kachel und nicht darin: Ein Knopf innerhalb eines
                Links wäre weder auszulösen noch mit der Tastatur zu erreichen.
              */}
              <button
                className="button ghost icon board-card-copy"
                onClick={() => void duplicate(board.id)}
                disabled={!canCreate || busyBoardId !== null}
                aria-label={t('boards.duplicateLabel', { title: board.title })}
                title={canCreate ? t('boards.duplicateTitle') : t('license.expired.short')}
              >
                {busyBoardId === board.id ? '…' : '⧉'}
              </button>
            </li>
          ))}
        </ul>
      </main>

      {showNewBoard && current && (
        <NewBoardDialog
          orgId={current.id}
          design={current.design}
          onCreate={createBoard}
          onClose={() => setShowNewBoard(false)}
        />
      )}

      {showDesign && current && (
        <OrgDesignDialog
          org={current}
          onClose={() => setShowDesign(false)}
          onSaved={() => void reload()}
        />
      )}

      {showAccount && <AccountDialog me={user} onClose={() => setShowAccount(false)} />}

      {showAdmin && (
        <AdminDialog
          onClose={() => {
            setShowAdmin(false)
            void reload()
          }}
        />
      )}

      {showTeam && current && (
        <TeamDialog
          org={current}
          me={user}
          onClose={() => setShowTeam(false)}
          onChanged={() => void reload()}
        />
      )}

      <LegalFooter />
    </div>
  )
}
